import type {
  EvalComparisonDetailResponse,
  EvalJudgment,
} from "contracts/api/evals/comparisons";
import { ERROR_CODE } from "contracts/enums/api";
import {
  EVAL_COMPARISON_STATUS,
  EVAL_JUDGE_KIND,
  type EvalComparisonStatus,
  type EvalWinner,
} from "contracts/enums/evals";
import {
  EVAL_TIE_COPY,
  EVIDENCE_STATE,
  type EvidenceState,
  HUMAN_VERDICT_STATE,
  type HumanVerdictState,
} from "../../enums/evals";
import { ApiError } from "../../store/api-client";

/**
 * The interpretation layer for one comparison: what the pair's evidence means,
 * which verdict stands, whether an analyst may record one, and whether the
 * surface should keep asking. Pure functions over contract data — the screens
 * render these answers, they do not re-derive them.
 */

/** The two variant labels a batch chose, which is how a winner is named. */
export type VariantLabels = { a: string; b: string };

/**
 * A comparison read. `ready: false` is the backend's 409 while the pair is
 * still analysing — a state of the comparison, not a failed request, so it is
 * data rather than an error the surface has to decode.
 */
export type ComparisonRead =
  | { ready: true; detail: EvalComparisonDetailResponse }
  | { ready: false };

/** The part of a read the poll condition actually looks at. */
type ComparisonProgress =
  | {
      ready: true;
      detail: Pick<EvalComparisonDetailResponse, "status" | "judgments">;
    }
  | { ready: false };

/**
 * A comparison read the backend refused because the pair is not finished.
 * `GET /api/evals/comparisons/:id` answers 409 until both runs have a completed
 * attempt, which is progress rather than failure: the batch detail already
 * knows how far along the pair is, so the surface waits instead of shouting.
 *
 * Decided from the transport's typed status and the backend's own error code,
 * never from the message text.
 */
export function comparisonNotReady(cause: unknown): boolean {
  return (
    cause instanceof ApiError &&
    cause.status === 409 &&
    cause.code === ERROR_CODE.CONFLICT
  );
}

/** Nothing will change on this comparison without someone acting on it. */
const SETTLED_STATUSES: readonly EvalComparisonStatus[] = [
  EVAL_COMPARISON_STATUS.JUDGED,
  EVAL_COMPARISON_STATUS.FAILED,
];

/**
 * Whether the comparison is still expected to change on its own — the poll
 * condition.
 *
 * Readiness is not the end of the wait: a pair whose runs have both finished
 * still has no verdict until the judge writes one, and the page promises it
 * updates itself. So it keeps asking until a judgment exists or the comparison
 * has settled.
 */
export function comparisonStillMoving(read: ComparisonProgress | undefined) {
  if (read === undefined) return false;
  if (!read.ready) return true;
  if (read.detail.judgments.length > 0) return false;
  return !SETTLED_STATUSES.includes(read.detail.status);
}

export function evidenceState(matches: boolean | null): EvidenceState {
  if (matches === null) return EVIDENCE_STATE.INCOMPLETE;
  return matches ? EVIDENCE_STATE.IDENTICAL : EVIDENCE_STATE.DIVERGENT;
}

export function judgmentOfKind(
  judgments: readonly EvalJudgment[],
  kind: EvalJudgment["judgeKind"],
): EvalJudgment | null {
  return judgments.find((judgment) => judgment.judgeKind === kind) ?? null;
}

/**
 * The verdict that counts, and the one it displaced. A human judgment outranks
 * the model's wherever both exist — the same precedence the batch list's
 * standing winner uses, so the two surfaces cannot disagree about who won.
 */
export function standingVerdict(judgments: readonly EvalJudgment[]): {
  winner: EvalWinner;
  standing: EvalJudgment;
  overridden: EvalJudgment | null;
} | null {
  const human = judgmentOfKind(judgments, EVAL_JUDGE_KIND.HUMAN);
  const llm = judgmentOfKind(judgments, EVAL_JUDGE_KIND.LLM);
  const standing = human ?? llm;
  if (!standing) return null;
  return {
    winner: standing.winner,
    standing,
    overridden: human && llm ? llm : null,
  };
}

/**
 * Whether the human-verdict control is live. The backend accepts one human
 * judgment per comparison, and only once the pair has been judged or handed to
 * the judge; both conditions are visible in the payload, so the UI answers them
 * itself rather than probing with a write and reading the rejection.
 */
export function humanVerdictState(
  detail: Pick<EvalComparisonDetailResponse, "status" | "judgments">,
): HumanVerdictState {
  if (judgmentOfKind(detail.judgments, EVAL_JUDGE_KIND.HUMAN)) {
    return HUMAN_VERDICT_STATE.RECORDED;
  }
  const judgeable: readonly EvalComparisonStatus[] = [
    EVAL_COMPARISON_STATUS.JUDGE_REQUESTED,
    EVAL_COMPARISON_STATUS.JUDGED,
  ];
  return judgeable.includes(detail.status)
    ? HUMAN_VERDICT_STATE.OPEN
    : HUMAN_VERDICT_STATE.TOO_EARLY;
}

/**
 * A winner named with the batch's own variant labels, because "a" means nothing
 * to the operator who called one side "control" and the other "deepseek
 * candidate". A tie is the one verdict whose wording is not a variant's name.
 *
 * The record is exhaustive over the winner enum, so a new member fails to
 * compile until it is named here.
 */
export function winnerLabel(winner: EvalWinner, labels: VariantLabels): string {
  const names: Record<EvalWinner, string> = {
    a: labels.a,
    b: labels.b,
    tie: EVAL_TIE_COPY,
  };
  return names[winner];
}
